import { useMutation } from '@apollo/client'
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_EMAIL_RATE_CONFIRMATION } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import { hasDuplicateEmails, isEmail } from '../../../utils/common'
import type { AppModalProps } from '../apps-data'

interface EmailRateConfirmationResponse {
  enableEmailRateConfirmationApp: {
    rateConfirmationEmail: string
  }
}

const formSchema = z.object({
  emailAddresses: z.string().min(1, 'At least one email address is required')
})

/**
 * This is the modal for the EnableEmailRateConfirmationModal app.
 */
export function EnableEmailRateConfirmationModal({ isOpen, onClose, appType }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [emails, setEmails] = useState<string[]>([])

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      emailAddresses: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableEmailRateConfirmation, { loading }] = useMutation<EmailRateConfirmationResponse>(
    ENABLE_EMAIL_RATE_CONFIRMATION,
    {
      context: {
        headers: {
          'ws-id': workspaceID
        }
      },
      onCompleted: (data) => {
        toast.success(
          `Email for sending shipment updates has been successfully configured. You can use this email: ${data.enableEmailRateConfirmationApp.rateConfirmationEmail}`
        )
        handleClose()
      },
      onError: (err) => {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        toast.error(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
      },
      refetchQueries: [GET_APPS]
    }
  )

  const handleClose = () => {
    setFormError('')
    form.reset()
    setEmails([])
    onClose()
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    form.setValue('emailAddresses', input)
    const parsedEmails = input.split(',').map((email) => email.trim())
    setEmails(parsedEmails)
  }

  const onSubmit = () => {
    // Validate each email in the list
    const invalidEmails = emails.filter((email) => !isEmail(email))

    if (invalidEmails.length > 0) {
      toast.error(`Invalid email addresses: ${invalidEmails.join(', ')}`)
      return
    }

    const isDuplicate = hasDuplicateEmails(emails)
    if (isDuplicate) {
      toast.error('Duplicate email addresses found')
      return
    }

    enableEmailRateConfirmation({
      variables: {
        allowedEmailAddresses: emails,
        appType: appType as AppTypeEnum
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>What emails will the rate confirmations come from?</DialogTitle>
          <DialogDescription>
            Enter one or more emails that will send the rate confirmations. We will provide you with a unique email
            where you can forward them to automatically create the load in Chain.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="emailAddresses"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Addresses</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter one or more emails separated by commas"
                      {...field}
                      onChange={handleEmailChange}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                  <div className="flex flex-wrap gap-2 pt-2">
                    {emails.map((email, index) => {
                      const isValidEmail = isEmail(email)
                      if (email.trim() === '') return null
                      return (
                        <Badge key={index} variant={isValidEmail ? 'default' : 'destructive'}>
                          {email}
                        </Badge>
                      )
                    })}
                  </div>
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter>
              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
